import React from "react";
import EditIcon from '@material-ui/icons/Edit';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';
import { Field } from 'redux-form';
import IconWithButton from "../../../common/IconWithButton/IconWithButton";
import ReduxFormDropdownField, { ReduxDropdownProps } from "../../../common/reduxFormFields/ReduxFormDropDownField";
import ReduxFormTextField from "../../../common/reduxFormFields/ReduxFormTextField";
import ReduxFormValidationError from '../../../common/reduxFormFields/ReduxFormValidationError';
import CustomTable from "../../../common/table/CustomTable";
import { ClassicDdsFields } from "./DdsFieldsGroup";
import { FormConstants, RequestorFormConstants } from './DataEntryFieldsConstants';
import { DISABILITY_OR_SOCIAL_SECURITY, INTERNAL } from "../../../models/requestType";
import { Contact, UNITED_STATES } from '../../../models/misc';
import { Requestor } from '../../../models/requestor';
import "../DataEntryDetails.scss";

/**
 * RequestorContactPanel — the Classic-mode "Requestor Information & Contacts"
 * section of the Data Entry requestor tab, extracted verbatim from
 * DataEntryDetailsRightPane (activeTabId === 2) with ZERO behavior change
 * (AB#35116 Phase 0, tidy-first).
 *
 * Presentational only: the redux-form <Field> elements bind via React context,
 * so this panel MUST be rendered inside the same reduxForm-decorated form as
 * its host. All form state, connected props, view flags and change/blur
 * handlers are supplied by the host through props and forwarded unchanged, so
 * a later phase can mount this SAME panel inside a Quick Entry modal that
 * provides the same reduxForm context.
 */
export interface RequestorContactPanelProps {
    // Redux-connected props (threaded from the host that owns the reduxForm form context)
    formState?: any,
    requestorSearchDetails?: any[],
    getRequestorSearchDetailsAPIStatus?: any,
    updateRequestorDetails?: (requestor?: Requestor) => void,
    resetRequestorContactList?: () => void,
    requestorContactDetails?: readonly Contact[],
    getContactSearchDetailsAPIStatus?: any,
    lastUsedRequestorContact?: any,
    organizationId?: number,
    transactionId?: string,
    // Local view state + derived flags (owned by the host)
    requestType: number,
    showRequestorGrid: boolean,
    showContactGrid: boolean,
    showAdvanceRequestorSearch: boolean,
    showAddNewRequestor: boolean,
    requestorFieldEnabled: boolean,
    enableEditReqContact: boolean,
    contactsLoaded: boolean,
    isDisableDDSField: boolean,
    errorMessage: string,
    searchEmail: string,
    searchPhone: string,
    requiredContactError: string,
    // Setters and change/blur handlers (owned by the host; forwarded unchanged)
    setRequestorGrid?: any,
    setContactGrid?: any,
    setSearchEmail?: any,
    setRequestorFieldEnabled?: any,
    setEnableEditReqContact?: any,
    setAddNewContact?: any,
    drErrorMessage?: any,
    docTypeErrorMessage?: any,
    rfErrorMessage?: any,
    onChangeRQID?: any,
    onBlurRQID?: any,
    onChangeTXSiteCode?: any,
    onBlurTXSiteCode?: any,
    onChangeDR?: any,
    onBlurDR?: any,
    onChangeSSN?: any,
    onBlurSSN?: any,
    onChangeDocType?: any,
    onBlurDocType?: any,
    onChangeRF?: any,
    onBlurRF?: any,
    onChangeNoBarcode?: any,
    onChangeDetailsOrBillingPaneField?: any,
    onRequestorTableRowClickHandler?: any,
    /**
     * AB#35116: when set, the results grid's "Add new Requestor" defers to it instead
     * of unlocking the classic inline requestor form. The QE host is chromeless - that
     * inline form isn't on screen - so it hands off to the QE create-requestor modal.
     * Undefined in Classic => classic behavior, unchanged.
     */
    onAddNewRequestorOverride?: () => void,
    /**
     * AB#35147: true when this panel is hosted by the Quick Entry requestor modal, where
     * the data-entry pane the search grid normally anchors to is visibility:hidden - so
     * the grid opens centred instead of stranded in a corner, and stays draggable from
     * there. Set from hostContext by DataEntryDetailsRightPane, like
     * onAddNewRequestorOverride above; unset in Classic, which keeps its anchored start.
     */
    centerRequestorGridOnOpen?: boolean,
    onClickSearch?: any,
    onChangeRQSiteCode?: any,
    onChangeSearchName?: any,
    onChangeFaxOrPhoneOrEmail?: any,
    onChangeSearchAddress?: any,
    onChangeSearchZip?: any,
    onChangeSearchRequestorName?: any,
    onChangeSearchContactName?: any,
    onChangeShowAdvanceRequestorSearch?: any,
    onChangeRequestorName?: any,
    onChangeAddress1?: any,
    onChangeAddress2?: any,
    onChangeCity?: any,
    onChangeState?: any,
    onChangeZip?: any,
    onBlurRequestorTabFields?: any,
    onClickEdit?: any,
    onContactTableRowClickHandler?: any,
    onChangeFirstName?: any,
    onChangeLastName?: any,
    onChangeEmail?: any,
    onChangePhone?: any,
    onChangeFax?: any,
}

enum InputFieldLength { CASE_NUMBER_MAX_LENGTH = 40 } // ponytail: mirror of host enum member used by the Case number field

const RequestorContactPanel: React.FC<RequestorContactPanelProps> = (props: RequestorContactPanelProps) => {
    const {
        requestType,
        showRequestorGrid,
        showContactGrid,
        showAdvanceRequestorSearch,
        showAddNewRequestor,
        requestorFieldEnabled,
        enableEditReqContact,
        contactsLoaded,
        isDisableDDSField,
        errorMessage,
        searchEmail,
        searchPhone,
        requiredContactError,
        setRequestorGrid,
        setContactGrid,
        setSearchEmail,
        setRequestorFieldEnabled,
        setEnableEditReqContact,
        setAddNewContact,
        drErrorMessage,
        docTypeErrorMessage,
        rfErrorMessage,
        onChangeRQID,
        onBlurRQID,
        onChangeTXSiteCode,
        onBlurTXSiteCode,
        onChangeDR,
        onBlurDR,
        onChangeSSN,
        onBlurSSN,
        onChangeDocType,
        onBlurDocType,
        onChangeRF,
        onBlurRF,
        onChangeNoBarcode,
        onChangeDetailsOrBillingPaneField,
        onRequestorTableRowClickHandler,
        onClickSearch,
        onChangeRQSiteCode,
        onChangeSearchName,
        onChangeFaxOrPhoneOrEmail,
        onChangeSearchAddress,
        onChangeSearchZip,
        onChangeSearchRequestorName,
        onChangeSearchContactName,
        onChangeShowAdvanceRequestorSearch,
        onChangeRequestorName,
        onChangeAddress1,
        onChangeAddress2,
        onChangeCity,
        onChangeState,
        onChangeZip,
        onBlurRequestorTabFields,
        onClickEdit,
        onContactTableRowClickHandler,
        onChangeFirstName,
        onChangeLastName,
        onChangeEmail,
        onChangePhone,
        onChangeFax,
    } = props;

    const headCellsRequestor = [
        { id: 'comments', label: '', commentField: true },
        { id: 'en', label: 'E/N' },
        { id: 'requestorName', label: 'REQUESTOR NAME' },
        { id: 'address', label: 'Address' },
        { id: 'city', label: 'City' },
        { id: 'state', label: 'State' },
        { id: 'zip', label: 'Zip' },
    ] as const;

    const headCellsContact = [
        { id: 'firstName', label: 'FIRST NAME' },
        { id: 'lastName', label: 'LAST NAME' },
        { id: 'phone', label: 'PHONE' },
        { id: 'fax', label: 'FAX' },
        { id: 'email', label: 'EMAIL' },
    ] as const;

    return (
        <>
                        <div style={{ flexDirection: 'column', flexWrap: 'nowrap' }} className='requestor-info-container'>
                            <div className="new-panel-header section-headers" style={{ width: '98%', marginLeft: 12 }}> {'Requestor Information'} </div>
                            {requestType == DISABILITY_OR_SOCIAL_SECURITY &&
                                (
                                    <>
                                        <ClassicDdsFields
                                            formState={props.formState}
                                            isDisableDDSField={isDisableDDSField}
                                            drErrorMessage={drErrorMessage}
                                            docTypeErrorMessage={docTypeErrorMessage}
                                            rfErrorMessage={rfErrorMessage}
                                            onChangeRQID={onChangeRQID}
                                            onBlurRQID={onBlurRQID}
                                            onChangeTXSiteCode={onChangeTXSiteCode}
                                            onBlurTXSiteCode={onBlurTXSiteCode}
                                            onChangeDR={onChangeDR}
                                            onBlurDR={onBlurDR}
                                            onChangeSSN={onChangeSSN}
                                            onBlurSSN={onBlurSSN}
                                            onChangeDocType={onChangeDocType}
                                            onBlurDocType={onBlurDocType}
                                            onChangeRF={onChangeRF}
                                            onBlurRF={onBlurRF}
                                            onChangeNoBarcode={onChangeNoBarcode}
                                        />
                                        <div className="data-entry case-number placeholder-style" style={{ width: '33.5%', marginTop: -8 }}>
                                            <Field
                                                name={FormConstants.CASE_NUMBER}
                                                component={ReduxFormTextField}
                                                props={{
                                                    label: "Case number",
                                                    placeholder: "123456789",
                                                    maxLength: InputFieldLength.CASE_NUMBER_MAX_LENGTH,
                                                    required: true,
                                                    onBlur: onChangeDetailsOrBillingPaneField,
                                                    suppressEnterSubmit: true
                                                }}
                                            />
                                        </div>
                                        <span className='horizontal-line' style={{ marginTop: '4%' }}></span>
                                    </>
                                )
                            }

                            {/* Same as option */}
                            {(props.lastUsedRequestorContact && props.organizationId === props.lastUsedRequestorContact.org && props.transactionId !== props.lastUsedRequestorContact.transactionId)
                                &&
                                <div className='same-as-option'>
                                    <p className='form-label same-as-label'>To use Requestor: {"  "}
                                        <span className='same-as-identity'>{props.lastUsedRequestorContact.requestorName}</span>
                                        - Contact:
                                        <span className='same-as-identity'>{(props.lastUsedRequestorContact.firstName || props.lastUsedRequestorContact.lastName) ? `${props.lastUsedRequestorContact.firstName} ${props.lastUsedRequestorContact.lastName}` : '(None)'} </span>
                                        for this transaction, click
                                    </p>
                                    <IconWithButton onButtonClick={() => onRequestorTableRowClickHandler(props.lastUsedRequestorContact)} buttonText={`Same As`} hideArrowIcon={true} isSameAs={true} />
                                </div>}

                            {/* Requestor Search */}
                            <div
                                className='filter-text'
                                style={{ flexBasis: '10%', marginBottom: 13 }}
                            >
                                <span
                                    style={{ marginLeft: 12 }}
                                    className={"requestor-filter-text" + (requestType == DISABILITY_OR_SOCIAL_SECURITY ? "requestor-filter-text-adj" : "")}
                                >
                                    Search for requestor
                                </span>
                            </div>

                            <div id='requestor-search-fields' className="row">
                                {requestType == DISABILITY_OR_SOCIAL_SECURITY && <div style={{ flexBasis: "33%" }}>
                                    <Field
                                        name={RequestorFormConstants.RQ_SITE_CODE}
                                        component={ReduxFormTextField}
                                        props={{
                                            label: "RQ Site Code",
                                            placeholder: "Site Code",
                                            maxLength: 10,
                                            onChange: onChangeRQSiteCode,
                                            errorMessage: "",
                                            suppressEnterSubmit: true
                                        }}
                                    />
                                </div>}
                                {/* If Request Type is Internal */}
                                {requestType == INTERNAL ? <div style={{ flexBasis: "25%" }}>
                                    <Field
                                        name={RequestorFormConstants.INTENRNAL_REQUESTOR}
                                        component={ReduxFormTextField}
                                        props={{
                                            label: "Name",
                                            placeholder: "Name",
                                            maxLength: 50,
                                            onChange: onChangeSearchName,
                                            suppressEnterSubmit: true
                                        }}
                                    />
                                </div>
                                    :
                                    <div id='phone/fax-search' style={{ flexBasis: requestType == DISABILITY_OR_SOCIAL_SECURITY ? "33%" : "25%" }}>
                                        <Field
                                            name={RequestorFormConstants.PHONE_FAX_NUMBER}
                                            component={ReduxFormTextField}
                                            props={{
                                                label: "Phone/Fax",
                                                placeholder: "XXX-XXX-XXXX",
                                                maxLength: 12,
                                                onChange: onChangeFaxOrPhoneOrEmail,
                                                errorMessage: !showAdvanceRequestorSearch && errorMessage.length > 0 ? errorMessage : null,
                                                suppressEnterSubmit: true
                                            }}
                                        />
                                    </div>}
                                <div id='email-search' style={{ flexBasis: requestType == DISABILITY_OR_SOCIAL_SECURITY ? "33%" : "25%" }}>
                                    <Field
                                        name={RequestorFormConstants.EMAIL_ADDRESS}
                                        component={ReduxFormTextField}
                                        props={{
                                            label: "Email",
                                            placeholder: "example@mail.com",
                                            maxLength: 50,
                                            onChange: (e: string) => { setSearchEmail(e); onChangeFaxOrPhoneOrEmail(e) },
                                            errorMessage: !showAdvanceRequestorSearch && requestType != INTERNAL && errorMessage.length > 0 ? "  " : null,
                                            suppressEnterSubmit: true
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        alignSelf: 'flex-start',
                                        marginTop: 21,
                                        marginLeft: requestType == DISABILITY_OR_SOCIAL_SECURITY ? 0 : 13,
                                        position: 'relative',
                                        flexBasis: requestType == DISABILITY_OR_SOCIAL_SECURITY ? 'auto' : '50%',
                                    }}
                                >
                                    <IconWithButton
                                        onButtonClick={onClickSearch}
                                        buttonText='Search'
                                        rotateArrow={showRequestorGrid ? 90 : 0}
                                    />
                                </div>
                            </div>

                            <div className='advanced-search-container' style={{ position: 'relative', display: 'flex', flexDirection: 'column-reverse', top: 0 }}>
                                {showAdvanceRequestorSearch &&
                                    <div>
                                        <div id='advanced-search-content' style={{ marginLeft: "0" }} className="row">
                                            <div id='address-search' style={{ flexBasis: requestType == DISABILITY_OR_SOCIAL_SECURITY ? "33%" : "25%" }}>
                                                <Field
                                                    name={RequestorFormConstants.SEARCH_ADDRESS}
                                                    component={ReduxFormTextField}
                                                    props={{
                                                        label: "Address",
                                                        placeholder: "123 main st",
                                                        maxLength: 70,
                                                        onChange: onChangeSearchAddress,
                                                        errorMessage,
                                                        suppressEnterSubmit: true
                                                    }}
                                                />
                                            </div>

                                            <div id='zip-search' style={{ flexBasis: requestType == DISABILITY_OR_SOCIAL_SECURITY ? "33%" : "25%" }}>
                                                <Field
                                                    name={RequestorFormConstants.SEARCH_ZIP}
                                                    component={ReduxFormTextField}
                                                    props={{
                                                        label: "Zip Code",
                                                        placeholder: "12345",
                                                        maxLength: 10,
                                                        onChange: onChangeSearchZip,
                                                        errorMessage: errorMessage.length > 0 ? "  " : null,
                                                        suppressEnterSubmit: true
                                                    }}
                                                />
                                            </div>

                                        </div>
                                        <div id='advanced-search-content' style={{ marginLeft: "0" }} className="row">
                                            <div id='requestor-name-search' style={{ flexBasis: requestType == DISABILITY_OR_SOCIAL_SECURITY ? "33%" : "25%" }}>
                                                <Field
                                                    name={RequestorFormConstants.SEARCH_REQUESTOR_NAME}
                                                    component={ReduxFormTextField}
                                                    props={{
                                                        label: "Requestor Name",
                                                        placeholder: "Name",
                                                        maxLength: 50,
                                                        onChange: onChangeSearchRequestorName,
                                                        errorMessage,
                                                        suppressEnterSubmit: true
                                                    }}
                                                />
                                            </div>

                                            <div id='requestor-contact-search' style={{ flexBasis: requestType == DISABILITY_OR_SOCIAL_SECURITY ? "33%" : "25%" }}>
                                                <Field
                                                    name={RequestorFormConstants.SEARCH_CONTACT_NAME}
                                                    component={ReduxFormTextField}
                                                    props={{
                                                        label: "Contact",
                                                        placeholder: "Contact",
                                                        maxLength: 50,
                                                        onChange: onChangeSearchContactName,
                                                        errorMessage: errorMessage.length > 0 ? "  " : null,
                                                        suppressEnterSubmit: true
                                                    }}
                                                />
                                            </div>

                                        </div></div>
                                }
                            </div>
                            <div
                                id='advanced-search-toggle'
                                className="row"
                                style={{
                                    justifyContent: requestType == DISABILITY_OR_SOCIAL_SECURITY ? 'flex-start' : 'space-between',
                                    flexDirection: requestType == DISABILITY_OR_SOCIAL_SECURITY ? 'column' : 'row',
                                    marginRight: requestType == DISABILITY_OR_SOCIAL_SECURITY ? '3px' : '-15px',
                                }}
                            >
                                {requestType != INTERNAL ?
                                    <div id='icon+label'>
                                        <span className='edit-contact'
                                            onClick={() => onChangeShowAdvanceRequestorSearch()}>
                                            <ArrowForwardIos style={{ fontSize: 18, transform: (showAdvanceRequestorSearch ? 'rotate(90deg)' : '') }} />Advanced Search
                                        </span>
                                    </div>
                                    :
                                    <div>
                                        <span></span>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className='search-requestor-table'>
                            {showRequestorGrid ?
                                <CustomTable
                                    onTableRowClickHandler={onRequestorTableRowClickHandler}
                                    setGrid={setRequestorGrid}
                                    rows={props.requestorSearchDetails ?? []}
                                    headCells={headCellsRequestor}
                                    addNewText="Add new Requestor"
                                    defaultSortCol="default"
                                    centerOnOpen={props.centerRequestorGridOnOpen}
                                    onAddNew={() => {
                                        if (props.onAddNewRequestorOverride) {
                                            props.onAddNewRequestorOverride();
                                            return;
                                        }
                                        props.updateRequestorDetails?.(undefined);
                                        setRequestorFieldEnabled(true);
                                        props.resetRequestorContactList!();
                                        setEnableEditReqContact(false);
                                    }}
                                    apiStatus={props.getRequestorSearchDetailsAPIStatus}
                                    showAddNew={showAddNewRequestor}
                                />
                                : null}
                        </div>
                        <span className="horizontal-line" style={{ height: 1, flexBasis: 'auto', marginTop: requestType == DISABILITY_OR_SOCIAL_SECURITY && showAdvanceRequestorSearch ? 23 : '4%' }}></span>

                        <div className='requestor-info-container requestor-input-container' style={{ marginTop: 8 }}>
                            <div className='data-entry requestor-info placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.REQUESTOR_NAME}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "Requestor name",
                                        required: true,
                                        placeholder: "Requestor Name",
                                        maxLength: 100,
                                        onChange: onChangeRequestorName,
                                        onBlur: onBlurRequestorTabFields,
                                        disabled: !requestorFieldEnabled,
                                        suppressEnterSubmit: true
                                    }}
                                />
                            </div>

                            <div className='data-entry requestor-info placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.ADDRESS}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "Address",
                                        required: true,
                                        placeholder: "Address line 1",
                                        maxLength: 70,
                                        onChange: onChangeAddress1,
                                        onBlur: onBlurRequestorTabFields,
                                        disabled: !requestorFieldEnabled,
                                        suppressEnterSubmit: true
                                    }}
                                />
                            </div>

                            <div className='data-entry requestor-addressLine2 placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.ADDRESS2}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "",
                                        maxLength: 70,
                                        placeholder: "P.O. Box, Suite, Apt #",
                                        onChange: onChangeAddress2,
                                        onBlur: onBlurRequestorTabFields,
                                        disabled: !requestorFieldEnabled,
                                        suppressEnterSubmit: true

                                    }}
                                />
                            </div>

                            <div className='data-entry requestor-city placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.CITY}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "City",
                                        required: true,
                                        placeholder: "City",
                                        maxLength: 30,
                                        onChange: onChangeCity,
                                        onBlur: onBlurRequestorTabFields,
                                        disabled: !requestorFieldEnabled,
                                        suppressEnterSubmit: true

                                    }}
                                />
                            </div>

                            <div className='data-entry requestor-state placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.STATE}
                                    component={ReduxFormDropdownField}
                                    props={{
                                        label: "State",
                                        required: true,
                                        options: [
                                            { label: "- Select -", value: "", disabled: true, hidden: true },
                                            ...UNITED_STATES.map(st => ({ label: st.abbreviation, value: st.abbreviation })),
                                        ],
                                        placeholder: "- Select -",
                                        onChange: onChangeState,
                                        onBlur: onBlurRequestorTabFields,
                                        disabled: !requestorFieldEnabled,
                                        suppressEnterSubmit: true
                                    } as ReduxDropdownProps}
                                />
                            </div>
                            <div className='data-entry requestor-zip placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.ZIP}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "Zip",
                                        required: true,
                                        placeholder: "Zip code",
                                        maxLength: 5,
                                        onChange: onChangeZip,
                                        onBlur: onBlurRequestorTabFields,
                                        disabled: !requestorFieldEnabled,
                                        suppressEnterSubmit: true

                                    }}
                                />
                            </div>
                        </div>

                        <div className='contact-info-container' id='requestorContactForm'>
                            <div className='panel-header-and-edit'>
                                <div className="new-panel-header section-headers" style={{ width: '98%', marginLeft: 12 }}>
                                    Contacts
                                </div>
                                <div className='data-entry' style={{ position: 'absolute', right: 12 }}>
                                    <span className={!contactsLoaded || showContactGrid ? 'edit-contact edit-contact-disabled' : 'edit-contact edit-contact-enabled'}
                                        onClick={contactsLoaded ? () => onClickEdit() : () => { }}>
                                        <EditIcon className={!contactsLoaded || showContactGrid ? 'edit-icon-disabled' : 'edit-icon-enabled'} />
                                        Assign Contact
                                    </span>
                                </div>
                            </div>
                            <Field
                                name={RequestorFormConstants.REQUESTOR_CONTACT}
                                component={ReduxFormValidationError}
                                props={{
                                    errorMessage: requiredContactError,
                                    suppressEnterSubmit: true
                                }}
                            />
                            {showContactGrid ?
                                <CustomTable
                                    onTableRowClickHandler={onContactTableRowClickHandler}
                                    setGrid={setContactGrid}
                                    rows={props.requestorContactDetails ?? []}
                                    headCells={headCellsContact}
                                    addNewText="Add new Contact"
                                    defaultSortCol={searchEmail ? "email" : (searchPhone ? "phonefax" : "firstName")}
                                    defaultSortVal={searchEmail || searchPhone}
                                    onAddNew={() => {
                                        setAddNewContact(true)
                                        setEnableEditReqContact(true)
                                    }}
                                    showAddNew={true}
                                    apiStatus={props.getContactSearchDetailsAPIStatus}
                                />
                                : null}


                            <div className='data-entry requestor-first-name placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.FIRST_NAME}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "First name",
                                        placeholder: "First Name",
                                        maxLength: 30,
                                        onchange: onChangeFirstName,
                                        onBlur: (v, f, e) => {
                                            onChangeFirstName(v, true);
                                            onBlurRequestorTabFields(v, f, e);
                                        },
                                        suppressEnterSubmit: true,
                                        disabled: !enableEditReqContact

                                    }}
                                />
                            </div>
                            <div className='data-entry requestor-last-name placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.LAST_NAME}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "Last name",
                                        placeholder: "Last Name",
                                        maxLength: 30,
                                        onchange: onChangeLastName,
                                        onBlur: (v, f, e) => {
                                            onChangeLastName(v, true);
                                            onBlurRequestorTabFields(v, f, e);
                                        },
                                        suppressEnterSubmit: true,
                                        disabled: !enableEditReqContact

                                    }}
                                />
                            </div>
                            <div className='data-entry placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.EMAIL}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "Email",
                                        placeholder: "example@mail.com",
                                        maxLength: 50,
                                        onchange: onChangeEmail,
                                        onBlur: (v, f, e) => {
                                            onChangeEmail(v, true);
                                            onBlurRequestorTabFields(v, f, e);
                                        },
                                        suppressEnterSubmit: true,
                                        disabled: !enableEditReqContact,
                                        errorMessage: props.formState.syncErrors && props.formState.syncErrors[RequestorFormConstants.EMAIL]

                                    }}
                                />
                            </div>

                            <div className='data-entry placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.PHONE_NUMBER}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "Phone number",
                                        placeholder: "XXX-XXX-XXXXxXXXXX",
                                        maxLength: 30,
                                        onchange: onChangePhone,
                                        onBlur: (v, f, e) => {
                                            onChangePhone(v, true);
                                            onBlurRequestorTabFields(v, f, e);
                                        },
                                        suppressEnterSubmit: true,
                                        disabled: !enableEditReqContact,
                                        errorMessage: props.formState.syncErrors && props.formState.syncErrors[RequestorFormConstants.PHONE_NUMBER]
                                    }}
                                />
                            </div>
                            <div className='data-entry placeholder-style'>
                                <Field
                                    name={RequestorFormConstants.FAX_NUMBER}
                                    component={ReduxFormTextField}
                                    props={{
                                        label: "Fax number",
                                        placeholder: "XXX-XXX-XXXX",
                                        maxLength: 12,
                                        onchange: onChangeFax,
                                        onBlur: (v, f, e) => {
                                            onChangeFax(v, true);
                                            onBlurRequestorTabFields(v, f, e);
                                        },
                                        suppressEnterSubmit: true,
                                        disabled: !enableEditReqContact,
                                        errorMessage: props.formState.syncErrors && props.formState.syncErrors[RequestorFormConstants.PHONE_FAX_NUMBER]
                                    }}
                                />
                            </div>
                        </div>
        </>
    );
};

export default RequestorContactPanel;
